import MuiButton, { ButtonProps as Props } from '@material-ui/core/Button'
import grey from '@material-ui/core/colors/grey'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import * as React from 'react'

import Color from '../base/Color'

export type ButtonVariants = 'primary' | 'cta' | 'ghost' | 'basic'

export type StyledButton = {
  variants?: ButtonVariants
  customSize?: 'xs' | 'small' | 'medium' | 'large'
}

export type ButtonProps = Props & StyledButton

const smTypography = {
  fontSize: 13,
  lineHeight: '20px',
  letterSpacing: '-0.01em',
  fontWeight: 700
}

const mdTypography = {
  fontSize: 15,
  lineHeight: '20px',
  letterSpacing: '-0.02em',
  fontWeight: 500
}

const Disabled = {
  '&.Mui-disabled': {
    background: Color.primary[400],
    color: grey[500]
  }
}

const AllSizes = {
  XS: {
    paddingTop: 6,
    paddingBottom: 6,
    paddingRight: 8,
    paddingLeft: 8,
    ...smTypography
  },
  Small: {
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: 8,
    paddingLeft: 8,
    ...smTypography
  },
  Medium: {
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: 12,
    paddingLeft: 12,
    ...mdTypography
  },
  Large: {
    paddingTop: 14,
    paddingBottom: 14,
    paddingRight: 16,
    paddingLeft: 16,
    ...mdTypography
  }
}

const getSize = (size: any) => {
  switch (size) {
    case 'xs':
      return AllSizes.XS
    case 'small':
      return AllSizes.Small
    case 'medium':
      return AllSizes.Medium
    case 'large':
      return AllSizes.Large
    default:
      return AllSizes.Medium
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    Primary: (props: ButtonProps) => ({
      background: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover': {
        background: theme.palette.primary.main
      },
      ...getSize(props.customSize),
      ...Disabled
    }),
    CallToAction: (props: ButtonProps) => ({
      background: Color.base.cta,
      color: theme.palette.common.white,
      '&:hover': {
        background: Color.base.cta
      },
      ...getSize(props.customSize),
      ...Disabled
    }),
    Ghost: (props: ButtonProps) => ({
      background: Color.primary[400],
      color: theme.palette.primary.main,
      '&:hover': {
        background: Color.primary[400]
      },
      ...getSize(props.customSize),
      ...Disabled
    }),
    Basic: (props: ButtonProps) => ({
      background: 'transparent',
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: 'transparent'
      },
      ...getSize(props.customSize),
      ...Disabled
    })
  })
)

const Button: React.FC<ButtonProps> = (props) => {
  const classes = useStyles(props)
  const { variants, customSize, children, ...rest } = props
  const getClasses = () => {
    switch (variants) {
      case 'primary':
        return { root: classes.Primary }
      case 'cta':
        return { root: classes.CallToAction }
      case 'ghost':
        return { root: classes.Ghost }
      case 'basic':
        return { root: classes.Basic }
      default:
        return { root: classes.Basic }
    }
  }

  return (
    <MuiButton classes={getClasses()} {...rest}>
      {children}
    </MuiButton>
  )
}

export default Button
